import { useMutation } from '@apollo/client';
import React from 'react';
import type { VariablesOf } from '@graphql-typed-document-node/core';
import {
  TopicDetached,
  TopicMemberEditable,
  TopicPrivacy,
} from '../../graphql/generated/graphql';
import topicPath from '../../utils/url/topicPath';
import { useRouter } from '../../utils/router/v3Bridge';
import TopicModal from './TopicModal';
import useAppSelector, { useAppDispatch } from '../../app/redux/useAppSelector';
import { gql } from '../../graphql/generated/gql';
import { UIActions } from '../../ui/uiSlice';
import { legacyUpdateQueryList } from '../../utils/graphql/updateQueryList';
import { USER_MEMBERSHIP_TOPICS } from '../../user/useUserMembershipTopics';

const CREATE_TOPIC_MUTATION = gql(`
  mutation createTopic(
    $name: String!
    $description: Json
    $memberEditable: TopicMemberEditable
    $detached: TopicDetached
    $parentId: ID
    $privacy: TopicPrivacy
    $inheritParent: Boolean
  ) {
    createTopic(
      name: $name
      description: $description
      memberEditable: $memberEditable
      detached: $detached
      parentId: $parentId
      privacy: $privacy
      inheritParent: $inheritParent
    ) {
      id
    }
  }
`);

const DEFAULT_MEMBER_EDITABLE = TopicMemberEditable.All;
const DEFAULT_PRIVACY = TopicPrivacy.Open;
const DEFAULT_DETACHED = TopicDetached.None;

interface NewTopicModalProps {
  close: () => void;
  onCreate?: (
    newTopic: VariablesOf<typeof CREATE_TOPIC_MUTATION> & { id: string },
  ) => void | Promise<void>;
  name?: string;
  parentId?: string;
}

const NewTopicModal = ({
  close,
  name,
  parentId,
  onCreate,
}: NewTopicModalProps) => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const [createTopic] = useMutation(CREATE_TOPIC_MUTATION);
  const userId = useAppSelector((state) => state.session.user?.id);

  const onSubmit = async (
    values: VariablesOf<typeof CREATE_TOPIC_MUTATION>,
  ) => {
    const response = await createTopic({
      variables: {
        ...values,
        inheritParent:
          values.privacy !== TopicPrivacy.Private &&
          values.privacy !== TopicPrivacy.Secret,
        description: JSON.stringify(values.description),
      },
      update: (cache, { data }) => {
        const topic = data?.createTopic;
        legacyUpdateQueryList(
          {
            query: USER_MEMBERSHIP_TOPICS,
            variables: { userId },
          },
          'user.membershipTopics',
          'topic',
        )(cache, { data: { topic } });
      },
    });
    const id = response.data?.createTopic?.id;

    if (id) {
      if (onCreate) {
        onCreate({ ...values, id });
      } else {
        router.push({
          pathname: topicPath({ id, name: values.name }),
          state: { edit: true, new: true },
        });
      }
      dispatch(UIActions.closeModal({ reset: true }));
    }
  };

  return (
    <TopicModal
      close={close}
      name={name || ''}
      privacy={DEFAULT_PRIVACY}
      memberEditable={DEFAULT_MEMBER_EDITABLE}
      detached={DEFAULT_DETACHED}
      onSubmit={onSubmit}
      parentId={parentId}
      submitText="Create topic"
      title="Create a topic"
    />
  );
};

export default NewTopicModal;
